import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiDatabase from '../../../server';
import {
  generateColorByName,
  dateWordMonthShortHour,
} from '../../../helper/helper';
import { Building, PersonIcon } from '../../../assets/icons';

const EVENT_CONFIG = {
  creation_mission: { label: 'a créé la mission' },
  attribuer_mission: { label: 'a attribué la mission' },
  desassigner_mission: { label: 'a désassigné un candidat de la mission' },
  refuser_candidat_mission: { label: 'a refusé un candidat' },
  modifier_motif_refus_candidat: {
    label: "a modifié le motif de refus d'un candidat",
  },
  attribuer_mission_invitation: {
    label: "a accepté l'invitation de la mission",
  },
  refuser_candidat_mission_invitation: {
    label: "a refusé l'invitation d'un candidat",
  },
  modifier_detail_mission: { label: 'a modifié le détail de la mission' },
  cancel_mission: { label: 'a annulé la mission' },
  delete_mission: { label: 'a supprimé la mission' },
  notification_mission: { label: 'a envoyé une notification' },
  assignation_staff: { label: 'a assigné le suivi de la mission' },
  suspend_mission: { label: 'a suspendu le recrutement de la mission' },
  cancel_recruitment_mission: { label: 'a annulé le recrutement de la mission' },
  resume_mission: { label: 'a remis la mission en ligne' },
  interrupt_mission: { label: 'a interrompu la mission' },
  noshow_unassign_mission: { label: "a désattribué l'intérimaire suite à un No-Show" },
  creation_mission_from_interruption: { label: 'a créé la mission suite à une interruption' },
};

const FIELD_LABELS = {
  qualification: 'Qualification',
  period: 'Période',
  schedule: 'Planning',
  salary: 'Salaire',
  description: 'Description',
  contact: 'Contact',
  licences: 'Permis',
  languages: 'Langues',
  tools: 'Outils',
  reference: 'Référence',
  other: 'Motif',
  other_interim: 'Motif',
  activity: 'Activité',
  conditions: 'Conditions',
  address: 'Adresse',
};

const DAY_LETTERS = ['D', 'L', 'Ma', 'Me', 'J', 'V', 'S'];

const formatScheduleCompact = (schedule) => {
  if (!Array.isArray(schedule) || schedule.length === 0) return null;
  const monthList = [
    'janv', 'févr', 'mars', 'avr', 'mai', 'juin',
    'juil', 'août', 'sept', 'oct', 'nov', 'déc',
  ];
  return schedule.map((week) => {
    const days = [...(week.days || [])].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    if (days.length === 0) return { name: week.name || 'Semaine', items: [] };
    const firstDate = new Date(days[0].date);
    const lastDate = new Date(days[days.length - 1].date);
    const range =
      firstDate.getTime() === lastDate.getTime()
        ? `${firstDate.getDate()} ${monthList[firstDate.getMonth()]}`
        : `${firstDate.getDate()}-${lastDate.getDate()} ${monthList[lastDate.getMonth()]}`;
    const items = days.map((d) => {
      const dt = new Date(d.date);
      const dayLetter = DAY_LETTERS[dt.getDay()];
      const p = d.periods;
      if (!p || Object.keys(p).length === 0)
        return { day: dayLetter, label: '—' };
      if (p.periode_one_start != null && p.periode_one_end != null) {
        if (
          p.periode_one_start === '00:00' &&
          p.periode_one_end === '00:00'
        ) {
          return { day: dayLetter, label: 'Non travaillé' };
        }
        let label = `${p.periode_one_start}–${p.periode_one_end}`;
        if (p.periode_two_start && p.periode_two_end) {
          label += ` / ${p.periode_two_start}–${p.periode_two_end}`;
        }
        if (p.periode_three_start && p.periode_three_end) {
          label += ` / ${p.periode_three_start}–${p.periode_three_end}`;
        }
        return { day: dayLetter, label };
      }
      return { day: dayLetter, label: 'Non renseigné' };
    });
    return { name: week.name || 'Semaine', range, items };
  });
};

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const isSchedule =
      value[0]?.days && Array.isArray(value[0].days);
    if (isSchedule) return null;
    return value
      .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
      .join(', ');
  }
  if (typeof value === 'object') {
    if (value.start && value.end) {
      const fmtDate = (d) => {
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return String(d);
        return dt.toLocaleDateString('fr-FR');
      };
      return `${fmtDate(value.start)} → ${fmtDate(value.end)}`;
    }
    if (value.wage_per_hour !== undefined) return `${value.wage_per_hour}€/h`;
    if (value.name !== undefined)
      return value.name + (value.poste ? ` (${value.poste})` : '');
    if (value.street !== undefined || value.city !== undefined) {
      const parts = [
        value.street,
        value.complement,
        [value.postcode, value.city].filter(Boolean).join(' '),
        value.department,
      ].filter(Boolean);
      return parts.join(', ');
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const getActivityText = (activity) => {
  const config = EVENT_CONFIG[activity.type];
  let label = config?.label || activity.type;
  let extra = '';
  let linkData = null;

  const candidateName = activity.details?.candidate
    ? `${activity.details.candidate.firstname || ''} ${activity.details.candidate.lastname || ''}`.trim()
    : '';

  switch (activity.type) {
    case 'attribuer_mission':
      if (candidateName) extra = `à ${candidateName}`;
      break;
    case 'attribuer_mission_invitation':
      break;
    case 'desassigner_mission':
    case 'refuser_candidat_mission':
    case 'refuser_candidat_mission_invitation':
    case 'modifier_motif_refus_candidat':
      break;
    case 'cancel_mission':
    case 'delete_mission':
      if (activity.details?.reason)
        extra = `— Raison : ${activity.details.reason}`;
      break;
    case 'suspend_mission':
      break;
    case 'resume_mission':
      break;
    case 'cancel_recruitment_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      break;
    case 'interrupt_mission':
      if (activity.details?.reason)
        extra = `— Motif : ${activity.details.reason}`;
      break;
    case 'noshow_unassign_mission':
      break;
    case 'creation_mission_from_interruption':
      if (activity.details?.originalMissionId)
        linkData = {
          missionId: activity.details.originalMissionId,
          text: `— Mission d'origine : n°${activity.details.originalMissionIdMission}`,
        };
      break;
    case 'notification_mission': {
      const nbUsers = activity.details?.nbUsers || 0;
      const plural = nbUsers > 1 ? 's' : '';
      const vivierNames = activity.details?.vivierNames;
      if (activity.details?.notificationType === 'vivier' && vivierNames?.length > 0) {
        label = `a envoyé une notification à ${vivierNames.length} vivier${vivierNames.length > 1 ? 's' : ''} (${nbUsers} intérimaire${plural})`;
      } else {
        label = `a envoyé une notification globale (${nbUsers} intérimaire${plural})`;
      }
      break;
    }
    case 'assignation_staff': {
      const employee = activity.details?.employee;
      if (employee) {
        label = 'a assigné le suivi de la mission';
        extra =
          `à ${employee.firstname || ''} ${employee.lastname || ''}`.trim();
      } else {
        label = 'a retiré l\'assignation du suivi de la mission';
      }
      break;
    }
    default:
      break;
  }

  return { label, extra, linkData };
};

const GROUPABLE_TYPES = new Set([
  'refuser_candidat_mission',
  'refuser_candidat_mission_invitation',
  'modifier_motif_refus_candidat',
  'desassigner_mission',
]);

const getAdminKey = (admin) =>
  admin ? `${admin.firstname || ''}|${admin.lastname || ''}` : '';

const groupActivities = (activities) => {
  if (!activities || activities.length === 0) return [];
  const groups = [];
  let current = null;

  for (const activity of activities) {
    const key = `${activity.type}|${getAdminKey(activity.admin)}`;
    if (
      current &&
      current.key === key &&
      GROUPABLE_TYPES.has(activity.type)
    ) {
      current.items.push(activity);
    } else {
      current = { key, items: [activity] };
      groups.push(current);
    }
  }

  return groups.map((g) => {
    if (g.items.length === 1) return g.items[0];
    const first = g.items[0];
    const candidateNames = g.items
      .map((a) => {
        const c = a.details?.candidate;
        return c
          ? `${c.firstname || ''} ${c.lastname || ''}`.trim()
          : null;
      })
      .filter(Boolean);
    return {
      ...first,
      _grouped: true,
      _count: g.items.length,
      _candidates: candidateNames,
      _lastDate: g.items[g.items.length - 1].date,
    };
  });
};

const getGroupedText = (activity) => {
  const config = EVENT_CONFIG[activity.type];
  const count = activity._count;

  switch (activity.type) {
    case 'refuser_candidat_mission':
      return `a refusé ${count} candidat${count > 1 ? 's' : ''}`;
    case 'refuser_candidat_mission_invitation':
      return `a refusé l'invitation de ${count} candidat${count > 1 ? 's' : ''}`;
    case 'modifier_motif_refus_candidat':
      return `a modifié le motif de refus de ${count} candidat${count > 1 ? 's' : ''}`;
    case 'desassigner_mission':
      return `a désassigné ${count} candidat${count > 1 ? 's' : ''} de la mission`;
    default:
      return config?.label || activity.type;
  }
};

const ScheduleCompactBlock = ({ data }) => {
  if (!data || data.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {data.map((week, wi) => (
        <div
          key={wi}
          className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100"
        >
          <div className="font-medium text-gray-700 text-[11px]">
            {week.name}
            {week.range && (
              <span className="text-gray-500 font-normal ml-1">
                ({week.range})
              </span>
            )}
          </div>
          {week.items && week.items.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {week.items.map((item, di) => (
                <span
                  key={di}
                  className="inline-flex items-center gap-0.5 bg-white border border-gray-100 rounded px-1.5 py-0.5 text-[10px]"
                >
                  <span className="text-gray-500 font-medium w-5">
                    {item.day}
                  </span>
                  <span className="text-gray-700">{item.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ActivityCard = ({ activity, isLast }) => {
  const [, setSearchParams] = useSearchParams();
  const isCompany = activity.typeUser === 'user';
  const isInvitationAccepted = activity.type === 'attribuer_mission_invitation';

  const candidateName = activity.details?.candidate
    ? `${activity.details.candidate.firstname || ''} ${activity.details.candidate.lastname || ''}`.trim()
    : '';

  const displayName = isInvitationAccepted && candidateName
    ? candidateName
    : activity.admin
      ? `${activity.admin.firstname || ''} ${activity.admin.lastname || ''}`.trim()
      : isCompany
        ? 'Entreprise'
        : 'Administrateur';

  const adminInitials = isInvitationAccepted && candidateName
    ? `${(activity.details.candidate.firstname || '')[0] || ''}${(activity.details.candidate.lastname || '')[0] || ''}`.toUpperCase()
    : activity.admin
      ? `${(activity.admin.firstname || '')[0] || ''}${(activity.admin.lastname || '')[0] || ''}`.toUpperCase()
      : 'A';

  const avatarColor = generateColorByName(displayName);
  const isGrouped = activity._grouped;
  const { label, extra, linkData } = isGrouped
    ? { label: getGroupedText(activity), extra: '', linkData: null }
    : getActivityText(activity);

  const dateLabel = isGrouped && activity._lastDate
    ? `${dateWordMonthShortHour(activity._lastDate)} – ${dateWordMonthShortHour(activity.date)}`
    : dateWordMonthShortHour(activity.date);

  const showUserIcon = isInvitationAccepted;

  return (
    <div className="group">
      <div className="flex gap-3 px-3 py-4 hover:bg-gray-50 transition-colors rounded-lg">
        <div className="flex-shrink-0 pt-0.5">
          {showUserIcon ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 border border-gray-200">
              <PersonIcon wh={16} color="#6B7280" />
            </div>
          ) : isCompany ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 border border-gray-200">
              <Building wh={16} color="#6B7280" />
            </div>
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium text-white"
              style={{ backgroundColor: avatarColor }}
            >
              {adminInitials}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {displayName}
            </span>
            <span className="text-xs text-gray-400">
              · {dateLabel}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-0">
            {label}
            {extra && <span className="text-gray-500"> {extra}</span>}
            {linkData && (
              <span
                className="text-yellow-600 font-semibold cursor-pointer hover:underline ml-1"
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.set('mission', linkData.missionId);
                    return prev;
                  });
                }}
              >
                {linkData.text}
              </span>
            )}
          </p>
          {isGrouped &&
            activity._candidates &&
            activity._candidates.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {activity._candidates.map((name, i) => (
                  <span
                    key={i}
                    className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200 rounded-md"
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          {!isGrouped &&
            GROUPABLE_TYPES.has(activity.type) &&
            activity.details?.candidate && (
              <div className="flex flex-wrap gap-1 mt-1">
                <span
                  className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200"
                  style={{ borderRadius: '2px' }}
                >
                  {`${activity.details.candidate.firstname || ''} ${activity.details.candidate.lastname || ''}`.trim()}
                </span>
              </div>
            )}
          {activity.type === 'notification_mission' &&
            activity.details?.notificationType === 'vivier' &&
            activity.details?.vivierNames?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {activity.details.vivierNames.map((name, i) => (
                  <span
                    key={i}
                    className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200"
                    style={{ borderRadius: '2px' }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          {activity.type === 'modifier_detail_mission' &&
            Array.isArray(activity.details?.modifications) &&
            activity.details.modifications.length > 0 && (
              <div className="mt-2 border border-gray-100 rounded-lg overflow-hidden">
                {activity.details.modifications.map((change, i) => {
                  const isSchedule = change.field === 'schedule';
                  const beforeSchedule =
                    isSchedule && Array.isArray(change.before)
                      ? formatScheduleCompact(change.before)
                      : null;
                  const afterSchedule =
                    isSchedule && Array.isArray(change.after)
                      ? formatScheduleCompact(change.after)
                      : null;

                  return (
                    <div
                      key={i}
                      className={`flex flex-col gap-2 px-3 py-2 text-xs ${
                        i !== activity.details.modifications.length - 1
                          ? 'border-b border-gray-100'
                          : ''
                      }`}
                    >
                      <span className="font-medium text-gray-700">
                        {FIELD_LABELS[change.field] || change.field}
                      </span>
                      {isSchedule && (beforeSchedule || afterSchedule) ? (
                        <div className="flex gap-4 flex-wrap">
                          <div className="flex-1 min-w-[140px]">
                            <div className="text-[10px] uppercase text-red-600 font-medium mb-1">
                              Avant
                            </div>
                            {beforeSchedule && beforeSchedule.length > 0 ? (
                              <ScheduleCompactBlock data={beforeSchedule} />
                            ) : (
                              <span className="text-gray-400 text-[11px]">
                                Aucun planning
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-[140px]">
                            <div className="text-[10px] uppercase text-green-600 font-medium mb-1">
                              Après
                            </div>
                            {afterSchedule && afterSchedule.length > 0 ? (
                              <ScheduleCompactBlock data={afterSchedule} />
                            ) : (
                              <span className="text-gray-400 text-[11px]">
                                Aucun planning
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded line-through">
                            {formatFieldValue(change.before)}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
                            {formatFieldValue(change.after)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>
      {!isLast && <hr className="border-gray-100" />}
    </div>
  );
};

const MissionActivityTab = ({ missionId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchActivities = (pageNum, append = false) => {
    const token = localStorage.getItem('xsrfToken');
    setLoading(true);
    ApiDatabase.getMissionActivityLogs(
      { token, idMission: missionId, page: pageNum, limit: 30 },
      (data) => {
        if (append) {
          setActivities((prev) => [...prev, ...data.activities]);
        } else {
          setActivities(data.activities);
        }
        setTotalPages(data.totalPages);
        setTotal(data.total);
        setLoading(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
        }
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (missionId) {
      setPage(1);
      fetchActivities(1);
    }
  }, [missionId]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchActivities(nextPage, true);
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex flex-col gap-4 py-4 px-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse flex gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex gap-2">
                <div className="w-24 h-4 bg-gray-200 rounded" />
                <div className="w-16 h-4 bg-gray-200 rounded-full" />
              </div>
              <div className="w-3/4 h-3 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!loading && activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <ClockEmptyIcon />
        </div>
        <div className="text-gray-900 font-semibold text-base mb-1">
          Aucune activité
        </div>
        <p className="text-gray-500 text-sm text-center">
          Aucune activité n'a encore été enregistrée pour cette mission.
        </p>
      </div>
    );
  }

  const grouped = groupActivities(activities);

  return (
    <div className="w-full py-2">
      <div>
        {grouped.map((activity, index) => (
          <ActivityCard
            key={index}
            activity={activity}
            isLast={index === grouped.length - 1}
          />
        ))}
      </div>
      {page < totalPages && (
        <div className="flex justify-center pt-4 pb-2">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="px-5 py-2 text-sm font-medium text-yellow-600 border border-yellow-200 rounded-full hover:bg-yellow-50 transition-colors disabled:opacity-50"
          >
            {loading ? 'Chargement...' : 'Voir plus'}
          </button>
        </div>
      )}
    </div>
  );
};

const ClockEmptyIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      stroke="#9CA3AF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default MissionActivityTab;
